import { isServer } from '@unifyapps/network/utils/fetch/utils';
import isNative from '@unifyapps/network/utils/native/isNative';

export const getterCache = new Map<string, (obj: unknown) => unknown>();

function setGetterCache(cacheKey: string, fn: (obj: unknown) => unknown) {
  if (isServer() && !isNative()) {
    return;
  }
  getterCache.set(cacheKey, fn);
}

/**
 * Compiles a path string into an optimized getter function.
 * Supports both dot notation and bracket notation:
 * - Dot notation: 'user.name'
 * - Bracket notation: 'user["name"]' or 'user[0]'
 * - Mixed notation: 'users[0].name' or 'users.0.name'
 *
 * @example
 * ```ts
 * const getter = compileGet('users[0].name');
 * const value = getter({ users: [{ name: 'John' }] }); // returns 'John'
 * ```
 */
export function compileGet(path: string) {
  // Handle empty path
  if (!path) return () => undefined;

  // Check cache first
  const cachedFn = getterCache.get(path);
  if (cachedFn) {
    // console.count(`cache hit in compileGet: ${path}`);
    return cachedFn;
  }

  try {
    // Create function that directly accesses the path with try-catch for safety
    // eslint-disable-next-line no-new-func -- fast path for getting data from object
    const fn = new Function('obj', `try { return obj.${path}; } catch { return undefined; }`) as (
      obj: unknown,
    ) => unknown;

    setGetterCache(path, fn);
    return fn;
  } catch (error) {
    console.error(`Error compiling getter for path: ${path}`, error);
    // Fallback if Function constructor fails (e.g., invalid path syntax)
    const fallbackFn = () => undefined;
    setGetterCache(path, fallbackFn);
    return fallbackFn;
  }
}

export function clearGetterCache() {
  getterCache.clear();
}
